import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { authService } from '../services/authService';
import { LanguageSelector } from './LanguageSelector';

interface PasswordAuthFormProps {
  mode: 'login' | 'register';
  onSuccess: (user: any, token: string) => void;
  onError: (error: string) => void;
}

export const PasswordAuthForm: React.FC<PasswordAuthFormProps> = ({
  mode,
  onSuccess,
  onError
}) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir() === 'rtl';
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    fullName: '',
    phoneNumber: '',
    primaryPlate: '',
    confirmPassword: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    let processedValue = value;
    
    // For username field, automatically remove leading '+' if present
    if (name === 'username' && value.startsWith('+')) {
      processedValue = value.slice(1); // Remove the leading '+'
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: processedValue
    }));
  };

  const validateForm = (): string | null => {
    if (!formData.username.trim()) return t('auth.passwordAuth.errors.usernameRequired');
    if (!formData.password.trim()) return t('auth.passwordAuth.errors.passwordRequired');
    
    if (mode === 'register') {
      if (!formData.fullName.trim()) return t('auth.passwordAuth.errors.fullNameRequired');
      if (!formData.phoneNumber.trim()) return t('auth.passwordAuth.errors.phoneNumberRequired');
      if (!formData.primaryPlate.trim()) return t('auth.passwordAuth.errors.licensePlateRequired');
      if (formData.password !== formData.confirmPassword) return t('auth.passwordAuth.errors.passwordMismatch');
      if (formData.password.length < 6) return t('auth.passwordAuth.errors.passwordTooShort');
      
      // Validate phone number (basic E.164 format)
      const phoneRegex = /^\+[1-9]\d{6,14}$/;
      if (!phoneRegex.test(formData.phoneNumber)) {
        return t('auth.passwordAuth.errors.phoneInvalidFormat');
      }
      
      // Validate license plate (nnn-nn-nnn format)
      const plateRegex = /^\d{3}-\d{2}-\d{3}$/;
      if (!plateRegex.test(formData.primaryPlate)) {
        return t('auth.passwordAuth.errors.plateInvalidFormat');
      }
    }
    
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const validation = validateForm();
    if (validation) {
      onError(validation);
      return;
    }

    setIsLoading(true);
    
    try {
      if (mode === 'login') {
        const result = await authService.loginWithPassword(formData.username, formData.password);
        onSuccess(result.user, result.token);
      } else {
        const result = await authService.registerWithPassword({
          username: formData.username,
          password: formData.password,
          fullName: formData.fullName,
          phoneNumber: formData.phoneNumber,
          primaryPlate: formData.primaryPlate,
          locale: 'en'
        });
        onSuccess(result.user, result.token);
      }
    } catch (error) {
      onError(error instanceof Error ? error.message : t('auth.passwordAuth.errors.authenticationFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="modal-overlay-modern">
      <div className={`modal-modern auth-modal ${isRTL ? 'rtl' : 'ltr'}`} dir={i18n.dir()}>        
        <div className="modal-header-modern">
          <h2 className="modal-title">
            {mode === 'login' ? t('auth.passwordAuth.title') : t('auth.passwordAuth.titleRegister')}
          </h2>
          <p className="modal-subtitle">
            {mode === 'login' 
              ? t('auth.passwordAuth.subtitle')
              : t('auth.passwordAuth.subtitleRegister')}
          </p>
        </div>
        
        <div className="modal-content-modern">
          <form onSubmit={handleSubmit} className="auth-form-modern">
            {/* Username */}
            <div className="form-field-modern">
              <label htmlFor="username" className="form-label-modern">
                {t('auth.passwordAuth.username')}
              </label>
              <input
                id="username"
                name="username"
                type="text"
                value={formData.username}
                onChange={handleInputChange}
                className="form-input-modern"
                placeholder={t('auth.passwordAuth.usernamePlaceholder')}
                disabled={isLoading}
                required
              />
            </div>

            {/* Password */}
            <div className="form-field-modern">
              <label htmlFor="password" className="form-label-modern">
                {t('auth.passwordAuth.password')}
              </label>
              <div className="form-input-with-icon-modern">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={handleInputChange}
                  className="form-input-modern form-input-with-button-modern"
                  placeholder={t('auth.passwordAuth.passwordPlaceholder')}
                  disabled={isLoading}
                  required
                  minLength={mode === 'register' ? 6 : undefined}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="form-input-icon-button-modern"
                  disabled={isLoading}
                  aria-label={showPassword ? t('auth.passwordAuth.hidePassword') : t('auth.passwordAuth.showPassword')}
                >
                  {showPassword ? '👁️' : '🔒'}
                </button>
              </div>
            </div>

            {/* Registration-only fields */}
            {mode === 'register' && (
              <>
                {/* Confirm Password */}
                <div className="form-field-modern">
                  <label htmlFor="confirmPassword" className="form-label-modern">
                    {t('auth.passwordAuth.confirmPassword')}
                  </label>
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    className="form-input-modern"
                    placeholder={t('auth.passwordAuth.confirmPasswordPlaceholder')}
                    disabled={isLoading}
                    required
                  />
                </div>

                {/* Full Name */}
                <div className="form-field-modern">
                  <label htmlFor="fullName" className="form-label-modern">
                    {t('auth.passwordAuth.fullName')}
                  </label>
                  <input
                    id="fullName"
                    name="fullName"
                    type="text"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    className="form-input-modern"
                    placeholder={t('auth.passwordAuth.fullNamePlaceholder')}
                    disabled={isLoading}
                    required
                  />
                </div>

                {/* Phone Number */}
                <div className="form-field-modern">
                  <label htmlFor="phoneNumber" className="form-label-modern">
                    {t('auth.passwordAuth.phoneNumber')}
                  </label>
                  <input
                    id="phoneNumber"
                    name="phoneNumber"
                    type="tel"
                    value={formData.phoneNumber}
                    onChange={handleInputChange}
                    className="form-input-modern"
                    placeholder={t('auth.passwordAuth.phoneNumberPlaceholder')}
                    disabled={isLoading}
                    required
                  />
                  <p className="form-help-text-modern">{t('auth.passwordAuth.phoneNumberHelp')}</p>
                </div>

                {/* License Plate */}
                <div className="form-field-modern">
                  <label htmlFor="primaryPlate" className="form-label-modern">
                    {t('auth.passwordAuth.licensePlate')}
                  </label>
                  <input
                    id="primaryPlate"
                    name="primaryPlate"
                    type="text"
                    value={formData.primaryPlate}
                    onChange={handleInputChange}
                    className="form-input-modern"
                    placeholder={t('auth.passwordAuth.licensePlatePlaceholder')}
                    disabled={isLoading}
                    required
                    pattern="^\d{3}-\d{2}-\d{3}$"
                  />
                  <p className="form-help-text-modern">{t('auth.passwordAuth.licensePlateHelp')}</p>
                </div>
              </>
            )}
          </form>
        </div>

        <div className="modal-actions-modern">
          <button
            type="submit"
            onClick={handleSubmit}
            disabled={isLoading}
            className="auth-button-base btn-primary-modern auth-submit-btn"
          >
            {isLoading ? (
              <span className="loading-content-modern">
                <svg className="loading-spinner-modern" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="loading-circle" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="loading-path" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {mode === 'login' ? t('auth.passwordAuth.signingIn') : t('auth.passwordAuth.creatingAccount')}
              </span>
            ) : (
              <>
                {mode === 'login' ? t('auth.passwordAuth.signInButton') : t('auth.passwordAuth.createAccountButton')}
              </>
            )}
          </button>

          {/* Link to dedicated registration page (only show in login mode) */}
          {mode === 'login' && (
            <Link 
              to="/register-password"
              className="auth-button-base btn-secondary-modern auth-register-link"
            >
              {t('auth.passwordAuth.createNewAccount')}
            </Link>
          )}

       
        </div>
           {/* Language Selector - Next Row Under Buttons */}
          <div className="auth-language-selector-bottom">
            <LanguageSelector variant="flags" showLabel={false} />
          </div>
      </div>
    </div>
  );
};